/**
 * Icon Box Section JavaScript
 * Handles animations, scroll snap functionality, and interactive features
 */

class IconBoxSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.animationType = section.dataset.animationType || 'fade-up';
    this.staggerAnimation = section.dataset.staggerAnimation === 'true';
    this.items = section.querySelectorAll('.icon-box__item');
    this.wrapper = section.querySelector('.icon-box__wrapper');
    this.dots = section.querySelectorAll('.icon-box__dot');
    this.isScrollSnap = this.wrapper?.dataset.scrollSnap === 'true';
    
    this.init();
  }

  init() {
    this.setupAnimations();
    this.setupScrollSnap();
    this.setupAccessibility();
  }

  /**
   * Setup scroll-triggered animations using Intersection Observer
   */
  setupAnimations() {
    if (!this.section.classList.contains('icon-box--animated')) return;
    if (!('IntersectionObserver' in window)) {
      this.items.forEach(item => item.classList.add('animated'));
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    };

    const animationDuration = parseInt(
      getComputedStyle(this.section).getPropertyValue('--icon-box-animation-duration') || '500'
    );
    
    const animationDelay = parseInt(
      getComputedStyle(this.section).getPropertyValue('--icon-box-animation-delay') || '100'
    );

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const item = entry.target;
          const order = parseInt(item.dataset.animationOrder) || 1;
          
          if (this.staggerAnimation) {
            setTimeout(() => {
              item.classList.add('animated');
            }, (order - 1) * animationDelay);
          } else {
            item.classList.add('animated');
          }
          
          observer.unobserve(item);
        }
      });
    }, observerOptions);

    this.items.forEach(item => observer.observe(item));
  }

  /**
   * Setup horizontal scroll snap functionality for mobile
   */
  setupScrollSnap() {
    if (!this.isScrollSnap || !this.wrapper || this.dots.length === 0) return;

    // Handle scroll events for dot updates
    let scrollTimeout;
    this.wrapper.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateActiveDot();
      }, 50);
    }, { passive: true });

    // Handle dot clicks
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.scrollToItem(index);
      });
    });

    // Touch swipe support
    this.setupTouchSwipe();
  }

  /**
   * Update active dot based on scroll position
   */
  updateActiveDot() {
    if (!this.wrapper) return;

    const scrollLeft = this.wrapper.scrollLeft;
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.wrapper).gap) || 0;
    const activeIndex = Math.round(scrollLeft / (itemWidth + gap));

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('icon-box__dot--active', index === activeIndex);
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }

  /**
   * Scroll to specific item
   */
  scrollToItem(index) {
    if (!this.wrapper || !this.items[index]) return;

    const item = this.items[index];
    const gap = parseInt(getComputedStyle(this.wrapper).gap) || 0;
    const scrollPosition = item.offsetLeft - (this.wrapper.offsetWidth - item.offsetWidth) / 2;

    this.wrapper.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }

  /**
   * Setup touch swipe gestures
   */
  setupTouchSwipe() {
    if (!this.wrapper) return;

    let startX = 0;
    let startScrollLeft = 0;
    let isDown = false;

    this.wrapper.addEventListener('touchstart', (e) => {
      isDown = true;
      startX = e.touches[0].pageX;
      startScrollLeft = this.wrapper.scrollLeft;
    }, { passive: true });

    this.wrapper.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      const x = e.touches[0].pageX;
      const walk = (startX - x);
      this.wrapper.scrollLeft = startScrollLeft + walk;
    }, { passive: true });

    this.wrapper.addEventListener('touchend', () => {
      isDown = false;
      // Snap to nearest item after swipe
      this.snapToNearestItem();
    });
  }

  /**
   * Snap to nearest item after scroll/swipe
   */
  snapToNearestItem() {
    if (!this.wrapper) return;

    const scrollLeft = this.wrapper.scrollLeft;
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.wrapper).gap) || 0;
    const nearestIndex = Math.round(scrollLeft / (itemWidth + gap));

    this.scrollToItem(Math.min(nearestIndex, this.items.length - 1));
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility() {
    // Add keyboard navigation for scroll snap
    if (this.isScrollSnap) {
      this.wrapper?.addEventListener('keydown', (e) => {
        const activeDotIndex = Array.from(this.dots).findIndex(
          dot => dot.classList.contains('icon-box__dot--active')
        );

        if (e.key === 'ArrowRight' && activeDotIndex < this.items.length - 1) {
          e.preventDefault();
          this.scrollToItem(activeDotIndex + 1);
        } else if (e.key === 'ArrowLeft' && activeDotIndex > 0) {
          e.preventDefault();
          this.scrollToItem(activeDotIndex - 1);
        }
      });
    }

    // Reduce motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.items.forEach(item => {
        item.classList.add('animated');
        item.style.transition = 'none';
      });
    }
  }
}

/**
 * Icon Box Manager - Handles multiple sections and Shopify events
 */
class IconBoxManager {
  constructor() {
    this.sections = new Map();
    this.init();
  }

  init() {
    // Initialize all existing sections
    document.querySelectorAll('[data-section-type="icon-box"]').forEach(section => {
      this.initSection(section);
    });

    // Listen for Shopify section events
    this.setupShopifyEvents();
  }

  initSection(section) {
    const sectionId = section.dataset.sectionId;
    if (sectionId && !this.sections.has(sectionId)) {
      this.sections.set(sectionId, new IconBoxSection(section));
    }
  }

  destroySection(sectionId) {
    if (this.sections.has(sectionId)) {
      this.sections.delete(sectionId);
    }
  }

  setupShopifyEvents() {
    // Section load
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-section-type="icon-box"]');
      if (section) {
        this.initSection(section);
      }
    });

    // Section unload
    document.addEventListener('shopify:section:unload', (event) => {
      const sectionId = event.detail?.sectionId;
      if (sectionId) {
        this.destroySection(sectionId);
      }
    });

    // Section reorder
    document.addEventListener('shopify:section:reorder', (event) => {
      const section = event.target.querySelector('[data-section-type="icon-box"]');
      if (section) {
        const sectionId = section.dataset.sectionId;
        this.destroySection(sectionId);
        this.initSection(section);
      }
    });

    // Block select (in theme editor)
    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      const section = block.closest('[data-section-type="icon-box"]');
      if (section) {
        // Scroll block into view
        const wrapper = section.querySelector('.icon-box__wrapper');
        if (wrapper && wrapper.dataset.scrollSnap === 'true') {
          const blockIndex = Array.from(wrapper.children).indexOf(block);
          const sectionInstance = this.sections.get(section.dataset.sectionId);
          if (sectionInstance) {
            sectionInstance.scrollToItem(blockIndex);
          }
        }
      }
    });
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.iconBoxManager = new IconBoxManager();
  });
} else {
  window.iconBoxManager = new IconBoxManager();
}

// Export for external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { IconBoxSection, IconBoxManager };
}
